import { useState, useMemo } from 'react';
import IntegrationCard from './IntegrationCard';
import { integrations, integrationCategories } from '../data/integrations';

interface IntegrationGridProps {
  searchQuery: string;
}

export default function IntegrationGrid({ searchQuery }: IntegrationGridProps) {
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Filter integrations based on search and category
  const filteredIntegrations = useMemo(() => {
    return integrations.filter((integration) => {
      const matchesSearch =
        searchQuery === '' ||
        integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        integration.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        integration.category.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory =
        selectedCategory === 'all' || integration.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  // Count integrations per category
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: integrations.length };
    integrations.forEach((integration) => {
      counts[integration.category] = (counts[integration.category] || 0) + 1;
    });
    return counts;
  }, []);

  return (
    <section className="py-20 bg-gradient-to-b from-white to-[#e4f4ff]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-6">
            All Integrations
          </h2>
          <p className="text-xl text-gray-700">
            Browse by category or search to find what you need
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-12">
          <div className="flex flex-wrap gap-3 justify-center">
            {integrationCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center gap-2 ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white shadow-lg'
                    : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-[#66b3ff]'
                }`}
              >
                <i className={category.icon}></i>
                {category.name}
                <span
                  className={`px-2 py-0.5 rounded-full text-xs ${
                    selectedCategory === category.id
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {categoryCounts[category.id] || 0}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        {searchQuery && (
          <div className="mb-8 text-center">
            <p className="text-gray-600">
              Found <span className="font-bold text-[#22577A]">{filteredIntegrations.length}</span> integration
              {filteredIntegrations.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </p>
          </div>
        )}

        {/* Integrations Grid */}
        {filteredIntegrations.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredIntegrations.map((integration) => (
              <IntegrationCard key={integration.id} integration={integration} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-search-line text-4xl text-gray-400"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-700 mb-4">
              No integrations found
            </h3>
            <p className="text-gray-600 mb-8">
              Try adjusting your search or browse all categories
            </p>
            <button
              onClick={() => setSelectedCategory('all')}
              className="px-6 py-3 bg-[#22577A] text-white rounded-lg font-semibold hover:bg-[#1a4460] transition-colors"
            >
              View All Integrations
            </button>
          </div>
        )}

        {/* Request Integration CTA */}
        <div className="mt-16 bg-white rounded-2xl border-2 border-dashed border-[#66b3ff] p-8 text-center">
          <div className="w-16 h-16 bg-[#e4f4ff]/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-add-circle-line text-3xl text-[#22577A]"></i>
          </div>
          <h3 className="text-2xl font-bold text-[#22577A] mb-4">
            Don't see your tool?
          </h3>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            We're constantly adding new integrations. Let us know what you need and we'll prioritize it for development.
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-lg font-semibold hover:shadow-xl transition-all cursor-pointer"
          >
            <i className="ri-mail-send-line"></i>
            Request an Integration
          </a>
        </div>
      </div>
    </section>
  );
}
